<template>
  <div class="space-y-4">
    <!-- Transactions Section -->
    <div
      class="bg-white dark:bg-gray-900 border-gray-100 dark:border-gray-700/50 rounded-2xl shadow-sm overflow-hidden"
    >
      <div class="flex items-center gap-2 mb-6">
        <div class="h-px bg-gray-200 dark:bg-gray-700 flex-1" />
        <h3 class="text-sm font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide">
          {{ t('app.views.cashRegister.transactions') }}
        </h3>
        <div class="h-px bg-gray-200 dark:bg-gray-700 flex-1" />
      </div>


      <!-- Transactions List - Mobile First -->
      <div
        class="bg-gray-50 dark:bg-gray-800/50 rounded-xl overflow-hidden"
      >
        <!-- Desktop Table View -->
        <div class="hidden lg:block overflow-x-auto">
          <table class="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead class="bg-gray-100 dark:bg-gray-900/50">
              <tr>
                <th
                  class="px-4 py-3 text-left text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wider"
                >
                  {{ t('app.views.cashRegister.date') }}
                </th>
                <th
                  class="px-4 py-3 text-left text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wider"
                >
                  {{ t('app.views.cashRegister.type') }}
                </th>
                <th
                  class="px-4 py-3 text-left text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wider"
                >
                  {{ t('app.views.cashRegister.description') }}
                </th>
                <th
                  class="px-4 py-3 text-left text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wider"
                >
                  {{ t('app.views.cashRegister.amount') }}
                </th>
                <th
                  class="px-4 py-3 text-left text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wider"
                >
                  {{ t('app.views.cashRegister.paymentMethod') }}
                </th>
              </tr>
            </thead>
            <tbody class="bg-white dark:bg-gray-800/30 divide-y divide-gray-100 dark:divide-gray-700/50">
              <tr
                v-for="transaction in paginatedTransactions"
                :key="transaction.id"
                class="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
              >
                <td class="px-4 py-3 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">
                  {{ formatDate(transaction.created_at) }}
                </td>
                <td class="px-4 py-3 whitespace-nowrap">
                  <span
                    class="inline-flex items-center px-2 py-1 rounded-lg text-xs font-medium border"
                    :class="getTransactionTypeBadgeClass(transaction.transaction_type)"
                  >
                    {{ translateTransactionType(transaction.transaction_type, t) }}
                  </span>
                </td>
                <td class="px-4 py-3">
                  <div class="text-sm">
                    <p class="font-medium text-gray-900 dark:text-gray-100">
                      {{ transaction.description }}
                    </p>
                    <p
                      v-if="transaction.notes"
                      class="text-xs text-gray-500 dark:text-gray-400 mt-1"
                    >
                      {{ transaction.notes }}
                    </p>
                  </div>
                </td>
                <td class="px-4 py-3 whitespace-nowrap text-sm">
                  <span
                    class="font-semibold"
                    :class="getAmountClass(transaction.transaction_type, transaction.amount)"
                  >
                    {{ getAmountPrefix(transaction.transaction_type) }}{{ formatCurrency(transaction.amount) }}
                  </span>
                </td>
                <td class="px-4 py-3 whitespace-nowrap">
                  <span
                    v-if="transaction.payment_method"
                    class="inline-flex items-center px-2 py-1 rounded-lg text-xs font-medium border"
                    :class="getPaymentMethodBadgeClass(transaction.payment_method)"
                  >
                    {{ translatePaymentMethod(transaction.payment_method, t) }}
                  </span>
                  <span
                    v-else
                    class="text-xs text-gray-500 dark:text-gray-400"
                  >
                    {{ t('app.common.na') }}
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Mobile Card View -->
        <div class="lg:hidden p-2">
          <div 
            v-for="transaction in paginatedTransactions" 
            :key="transaction.id"
            class="p-4 mb-3 hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors"
          >
            <!-- Header with type and amount -->
            <div class="flex items-start justify-between mb-3">
              <div class="flex-1">
                <span
                  class="inline-flex items-center px-2 py-1 rounded-lg text-xs font-medium border mb-2"
                  :class="getTransactionTypeBadgeClass(transaction.transaction_type)"
                >
                  {{ translateTransactionType(transaction.transaction_type, t) }}
                </span>
                <p class="font-medium text-gray-900 dark:text-gray-100 text-sm leading-tight">
                  {{ transaction.description }}
                </p>
              </div>
              <div class="ml-3 text-right">
                <span
                  class="font-semibold text-lg"
                  :class="getAmountClass(transaction.transaction_type, transaction.amount)"
                >
                  {{ getAmountPrefix(transaction.transaction_type) }}{{ formatCurrency(transaction.amount) }}
                </span>
              </div>
            </div>
            
            <!-- Footer with date and payment method -->
            <div class="flex items-center justify-between ">
              <div class="text-xs text-gray-500 dark:text-gray-400">
                {{ formatDate(transaction.created_at) }}
              </div>
              <div>
                <span
                  v-if="transaction.payment_method"
                  class="inline-flex items-center px-2 py-1 rounded-lg text-xs font-medium border"
                  :class="getPaymentMethodBadgeClass(transaction.payment_method)"
                >
                  {{ translatePaymentMethod(transaction.payment_method, t) }}
                </span>
              </div>
            </div>
          </div>
        </div>

        <!-- Empty State -->
        <EmptyState
          v-if="paginatedTransactions.length === 0"
          icon="document"
          :title="transactionSearchTerm || transactionTypeFilter
            ? t('app.views.cashRegister.noTransactionsFound')
            : t('app.views.cashRegister.noTransactions')
          "
          variant="minimal"
          size="md"
        />

        <!-- Pagination -->
        <div
          v-if="totalTransactionPages > 1"
          class="px-4 py-3 border-t border-gray-100 dark:border-gray-700/50 flex items-center justify-between bg-gray-50 dark:bg-gray-800/50"
        >
          <div class="text-sm text-gray-600 dark:text-gray-400">
            {{ t('app.views.cashRegister.showingResults', {
              start: transactionPagination.offset + 1,
              end: Math.min(transactionPagination.offset + transactionPagination.limit, filteredTransactions.length),
              total: filteredTransactions.length
            }) }}
          </div>
          <div class="flex items-center gap-2">
            <button
              type="button"
              class="px-3 py-1.5 text-sm border border-gray-200 dark:border-gray-600 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              :disabled="transactionPagination.page === 1"
              @click="$emit('previous-page')"
            >
              {{ t('app.common.previous') }}
            </button>
            <span class="text-sm text-gray-600 dark:text-gray-400 px-2">
              {{ transactionPagination.page }} / {{ totalTransactionPages }}
            </span>
            <button
              type="button"
              class="px-3 py-1.5 text-sm border border-gray-200 dark:border-gray-600 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              :disabled="transactionPagination.page === totalTransactionPages"
              @click="$emit('next-page')"
            >
              {{ t('app.common.next') }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Cuts Section -->
  <div
    v-if="session?.cuts && session.cuts.length > 0"
    class="space-y-4"
  >
    <div class="flex items-center gap-2 mb-6">
      <div class="h-px bg-gray-200 dark:bg-gray-700 flex-1" />
      <h3 class="text-sm font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide">
        {{ t('app.views.cashRegister.cuts') }}
      </h3>
      <div class="h-px bg-gray-200 dark:bg-gray-700 flex-1" />
    </div>
    <div class="space-y-3">
      <div
        v-for="cut in session.cuts"
        :key="cut.id"
        class="bg-white dark:bg-gray-800/30 border border-gray-100 dark:border-gray-700/50 rounded-2xl shadow-sm p-6"
      >
        <CutDetails :cut="cut" />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import { formatCurrency } from '@/utils/priceHelpers';
import { formatDateTime as formatDate } from '@/utils/dateHelpers';
import { translateTransactionType, translatePaymentMethod } from '@/utils/cashRegisterHelpers';
import SearchBar from '@/components/ui/SearchBar.vue';
import CutDetails from './CutDetails.vue';
import EmptyState from '@/components/base/EmptyState.vue';
import type { Session, Transaction, TransactionPagination } from './sessionDetailsTypes';

interface Props {
  session: Session | null;
  transactions: Transaction[];
  filteredTransactions: Transaction[];
  paginatedTransactions: Transaction[];
  totalTransactionPages: number;
  transactionPagination: TransactionPagination;
  transactionSearchTerm: string;
  transactionTypeFilter: string;
  getTransactionTypeBadgeClass: (type: string) => string;
  getPaymentMethodBadgeClass: (method: string) => string;
}

defineProps<Props>();

defineEmits<{
  (e: 'update:transactionSearchTerm', value: string): void;
  (e: 'update:transactionTypeFilter', value: string): void;
  (e: 'previous-page'): void;
  (e: 'next-page'): void;
}>();

const { t } = useI18n();

const getAmountClass = (type: string, amount: number) => {
  switch (type) {
    case 'sale':
      return 'text-green-600 dark:text-green-400';
    case 'expense':
      return 'text-red-600 dark:text-red-400';
    case 'cut':
      return 'text-blue-600 dark:text-blue-400';
    default:
      return 'text-gray-600 dark:text-gray-400';
  }
};

const getAmountPrefix = (type: string) => {
  switch (type) {
    case 'sale':
      return '+';
    case 'expense':
      return '-';
    case 'cut':
      return '-';
    default:
      return '';
  }
};
</script>
